import * as constants from '../../constants';
import { Plan } from '../../records/Plan';
import { PlanConfiguration } from '../../records/PlanConfiguration';

interface PlanCodeWithAddons {
  plan_code: string, // eslint-disable-line camelcase
  add_ons: { // eslint-disable-line camelcase
    code: string,
    quantity: number,
  }[]
}
/**
 * Maps order to plan codes with add_ons, unified checkout
 * @param {PlanConfiguration} orderItems
 * @returns {Object} plans shape for /billing/purchase
 */
export const getPlanCodesFromOrder = (orderItems): PlanCodeWithAddons[] => (
  orderItems.keySeq().toArray().map((planType): PlanCodeWithAddons => {
    let interval = constants.PLAN_CODE_INTERNVAL.ANNUAL;

    if (orderItems.get(planType)?.interval === constants.PLAN_INTERVAL.MONTH) {
      interval = constants.PLAN_CODE_INTERNVAL.MONTHLY;
    }

    let planCode = `unified-${planType as string}-${interval}`;
    if (planType === constants.USAGE_TYPE) {
      planCode = `usage-${interval}`;
    }
    let addOns;

    switch (planType) {
      case constants.PRODUCT_VDC:
      case constants.PRODUCT_RDC:
      case constants.PRODUCT_LIVE:
        addOns = [{
          code: constants.ADDON_PARALLEL_TESTS,
          quantity: orderItems.get(planType)?.[constants.CONCURRENCY_KEY],
        }];
        break;
      case constants.PRODUCT_API_TESTING:
        addOns = [{
          code: constants.ADDON_PROJECTS,
          quantity: orderItems.get(planType)?.[constants.PROJECTS_KEY],
        }];
        break;
      case constants.PRODUCT_USAGE:
        addOns = [{
          code: constants.ADDON_CREDIT_BLOCKS,
          quantity: orderItems.get(planType)?.[constants.CREDIT_BLOCKS_KEY],
        }];
        break;
      default: // addOns should remain undefined for Mobile Quality plans
        break;
    }

    return { add_ons: addOns, plan_code: planCode, plan_type: planType } as PlanCodeWithAddons;
  }) as PlanCodeWithAddons[]
);

/**
 * Get testing type from planCode.
 * `planCode` follows a specific format: unified-<plancode>-<interval>.
 * usage is exception, hence return planCode if result of slice is nullish
 * usage `planCode` follows format : usage-<interval>.
 * Slice the plan code in a way that allows for extraction the part we care about.
 * @param planCode refers to Plan::plan_code
 * @returns testing type extracted from `planCode`
 */
const planCodeToTestingType = (planCode: string): string => planCode.replace(/unified-/gi, '').split('-').slice(0, -1).join('-');

/**
 * Maps order to plans with productAmount and interval.
 * @param {PlanConfiguration[]} orderItems
 * @param {Plan[]} plans
 */
export const orderItemsToSubscriptionPreviews = (
  orderItems: Array<PlanConfiguration>,
  plans: Array<Plan>,
): Array<SubscriptionPreview> => {
  if (!plans?.length) return [];
  const plansWithAddons = getPlanCodesFromOrder(orderItems);

  return plansWithAddons.reduce(
    (acc, { add_ons: addOns, plan_code: planCode, plan_type: planType }) => {
      const plan = plans.find((elem) => elem.plan_code === planCode);
      const { interval } = orderItems.get(planCodeToTestingType(planCode));
      const quantity = !addOns ? 1 : addOns.find(
        ({ code }) => constants.ADDON_CODES.includes(code)).quantity;
      acc[planType] = {
        interval,
        monthlyRate: plan.calculateMonthlyRate(quantity),
        periodRate: plan.calculatePeriodRate(quantity),
        plan,
        productAmount: plan.isUsage() ? plan.creditsPerPeriod(quantity) : quantity,
      };
      return acc;
    },
    {},
  );
};

/**
 * Returns any plans that are monthly
 * @param {Map<string, PlanConfiguration>} orderItems
 * @returns {[string]}
 */
export const getMonthlyPlans = (orderItems: Map<string, PlanConfiguration>): string[] => (
  [...orderItems.entries()]
    // eslint-disable-next-line no-unused-vars
    .filter(([, order]) => order.interval === constants.PLAN_INTERVAL.MONTH)
    .map(([plan]) => plan)
);

/**
 * Returns the number of bonus credits based on number of credit blocks
 * @param {number} creditBlocks
 * @returns {number}
 */
export const annualBonusCredits = (creditBlocks: number): number => (
  creditBlocks * constants.CREDITS_PER_BLOCK * constants.ANNUAL_BONUS_PERCENTAGE
);

/**
 * Returns the total number of credits including bonus credits (if eligible)
 * @param {number} credits
 * @param {bool} isAnnual
 * @returns {number}
 */
export const creditsConsideringAnnualBonus = (credits: number, isAnnual: boolean): number => {
  const bonusMultiplier = isAnnual ? (1 + constants.ANNUAL_BONUS_PERCENTAGE) : 1;
  return credits * bonusMultiplier;
};
